/**
 * SuspendModal — reason-gated suspend confirmation (Phase 1 Slice 3).
 *
 * Extracted from FleetPanel.jsx (Phase 2 P2 decomposition · 2026-06).
 * Behaviour is BYTE-IDENTICAL: same testids, ESC dismisses, autofocus
 * on the reason textarea, Confirm button stays disabled until the
 * reason has at least 2 characters (mirrors backend Pydantic
 * DriverSuspendRequest.reason min_length=2 / max_length=500).
 */
import React, { useEffect, useState } from "react";
import { Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const SuspendModal = ({ driver, onCancel, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onCancel?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 2) {
      toast.error("Please enter a reason.");
      return;
    }
    setBusy(true);
    try { await onConfirm?.(trimmed); } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="suspend-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-border p-5 w-full max-w-md space-y-3"
        data-testid="suspend-modal"
      >
        <div className="flex items-center gap-2">
          <Ban className="w-5 h-5 text-[#EF4444]" />
          <h3 className="font-heading text-lg font-black">Suspend driver?</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          <strong>{driver.name}</strong> ({driver.email}) will be removed from the
          dispatch pool and will see this reason on their dashboard. They can
          still sign in to upload updated documents.
        </p>
        <label className="block">
          <div className="label-mini text-muted-foreground mb-1">REASON</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Insurance certificate has lapsed — please re-upload before resuming work."
            maxLength={500}
            rows={3}
            className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#EF4444] resize-none"
            data-testid="suspend-modal-reason"
            autoFocus
          />
          <div className="text-[10px] text-muted-foreground mt-1">
            Shown to the driver verbatim in their dashboard banner and email.
          </div>
        </label>
        <div className="flex gap-2 pt-2 border-t border-border">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 text-xs px-3 py-2 border border-border hover:bg-secondary transition-colors disabled:opacity-60"
            data-testid="suspend-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy || reason.trim().length < 2}
            className="flex-1 text-xs px-3 py-2 bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
            data-testid="suspend-modal-confirm"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Suspend driver
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuspendModal;
